/**
 * Oil & Gas — Corridor Boundaries  (Step 1 of the 8-step intelligence loop)
 *
 * Register pipeline right-of-way (RoW) corridor polygons and line buffers.
 * Each corridor carries: pipeline ID, product type, buffer width, and operator.
 *
 * The geometry is drawn on a Leaflet map and stored as a PostGIS polygon —
 * it is the region-of-interest sent to GEE for Sentinel-2 queries (Step 2).
 *
 * Features:
 *  - List / Map view toggle
 *  - Add: draw polygon on map → fill metadata → save
 *  - Edit: full form panel with optional geometry redraw
 *  - Map view: all corridors rendered with popup (Edit / Delete)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  PenTool, PlusCircle, Flame, RefreshCw, Trash2, Pencil,
  Check, Map, List,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { satelliteBoundariesApi } from '../../services/api';

// Fix Leaflet default icons in Vite builds
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface Corridor {
  id: string;
  name: string;
  description?: string;
  area_hectares?: number;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
  created_at: string;
  geometry?: { type: 'Polygon'; coordinates: number[][][] };
  metadata?: {
    pipeline_id?: string;
    product_type?: string;
    buffer_width_m?: number;
    operator?: string;
    vertical?: string;
  };
}

interface CorridorFormState {
  name: string;
  pipeline_id: string;
  product_type: string;
  buffer_width_m: string;
  operator: string;
  monitoring_frequency: string;
}

const EMPTY_FORM: CorridorFormState = {
  name: '',
  pipeline_id: '',
  product_type: 'crude_oil',
  buffer_width_m: '100',
  operator: '',
  monitoring_frequency: 'weekly',
};

const PRODUCT_TYPES = [
  { value: 'crude_oil',        label: 'Crude Oil' },
  { value: 'natural_gas',      label: 'Natural Gas' },
  { value: 'refined_products', label: 'Refined Products' },
  { value: 'condensate',       label: 'Condensate' },
  { value: 'water',            label: 'Produced Water' },
];

const MONITORING_FREQUENCIES = [
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'on_demand', label: 'On Demand' },
];

const CLS = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

// ─── FitBounds helper ─────────────────────────────────────────────────────────

const FitBounds: React.FC<{ corridors: Corridor[] }> = ({ corridors }) => {
  const map = useMap();
  useEffect(() => {
    const coords = corridors.flatMap((c) =>
      (c.geometry?.coordinates?.[0] ?? []).map((pt) => [pt[1], pt[0]] as [number, number])
    );
    if (coords.length) map.fitBounds(L.latLngBounds(coords), { padding: [50, 50] });
  }, [corridors, map]);
  return null;
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function corridorToForm(c: Corridor): CorridorFormState {
  return {
    name:                 c.name,
    pipeline_id:          c.metadata?.pipeline_id    ?? '',
    product_type:         c.metadata?.product_type   ?? 'crude_oil',
    buffer_width_m:       String(c.metadata?.buffer_width_m ?? 100),
    operator:             c.metadata?.operator        ?? '',
    monitoring_frequency: c.monitoring_frequency,
  };
}

function hasRealGeometry(c: Corridor): boolean {
  return (c.geometry?.coordinates?.[0]?.length ?? 0) > 2;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CorridorBoundariesPage: React.FC = () => {
  const [corridors, setCorridors] = useState<Corridor[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);
  const [viewMode,  setViewMode]  = useState<'list' | 'map'>('list');

  // Unified form state — editingId null = create, non-null = edit
  const [showForm,     setShowForm]     = useState(false);
  const [editingId,    setEditingId]    = useState<string | null>(null);
  const [form,         setForm]         = useState<CorridorFormState>(EMPTY_FORM);
  const [drawnLayer,   setDrawnLayer]   = useState<any>(null);

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await satelliteBoundariesApi.getAll();
      const all: Corridor[] = res.data?.boundaries ?? [];
      setCorridors(all.filter((b) => b.metadata?.vertical === 'oil_gas'));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load corridors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
    setShowForm(true);
    setViewMode('list');
  };

  const openEdit = (c: Corridor) => {
    setEditingId(c.id);
    setForm(corridorToForm(c));
    setDrawnLayer(null);
    setShowForm(true);
    setViewMode('list');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
  };

  const setF = (k: keyof CorridorFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  // ── Save (create or update) ───────────────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.pipeline_id.trim()) {
      setError('Corridor name and Pipeline ID are required');
      return;
    }
    if (!editingId && !drawnLayer) {
      setError('Please draw the corridor boundary on the map first');
      return;
    }
    setSaving(true); setError(null);
    try {
      const metadata = {
        pipeline_id:    form.pipeline_id.trim(),
        product_type:   form.product_type,
        buffer_width_m: Number(form.buffer_width_m) || 100,
        operator:       form.operator.trim(),
        vertical:       'oil_gas',
      };

      if (editingId) {
        await satelliteBoundariesApi.update(editingId, {
          name:                 form.name.trim(),
          description:          `Pipeline ${form.pipeline_id} — ${form.operator || 'unspecified operator'}`,
          monitoring_frequency: form.monitoring_frequency,
          metadata,
          ...(drawnLayer ? { geometry: drawnLayer.toGeoJSON().geometry } : {}),
        });
        setSuccess(`Corridor "${form.name}" updated`);
      } else {
        await satelliteBoundariesApi.create({
          name:                 form.name.trim(),
          description:          `Pipeline ${form.pipeline_id} — ${form.operator || 'unspecified operator'}`,
          geometry:             drawnLayer.toGeoJSON().geometry,
          monitoring_enabled:   true,
          monitoring_frequency: form.monitoring_frequency,
          metadata,
        });
        setSuccess(`Corridor "${form.name}" registered`);
      }
      closeForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save corridor');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete corridor "${name}"? This cannot be undone.`)) return;
    try {
      await satelliteBoundariesApi.deleteById(id);
      setSuccess(`Corridor "${name}" deleted`);
      if (editingId === id) closeForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to delete corridor');
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const validCorridors = corridors.filter(hasRealGeometry);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <PenTool className="w-7 h-7 text-orange-500" /> Corridor Boundaries
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 1 — Draw pipeline RoW polygons for satellite monitoring
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button
            variant={showForm && !editingId ? 'outline' : 'primary'}
            onClick={showForm && !editingId ? closeForm : openCreate}>
            {showForm && !editingId ? 'Cancel' : <><PlusCircle className="w-4 h-4 mr-1" /> Add Corridor</>}
          </Button>
        </div>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── View toggle (only when form is closed) ── */}
      {!showForm && (
        <div className="flex gap-2">
          <Button size="sm" variant={viewMode === 'list' ? 'primary' : 'outline'} onClick={() => setViewMode('list')}>
            <List className="w-4 h-4 mr-1" /> List
          </Button>
          <Button size="sm" variant={viewMode === 'map' ? 'primary' : 'outline'} onClick={() => setViewMode('map')}>
            <Map className="w-4 h-4 mr-1" /> Map
          </Button>
        </div>
      )}

      {/* ── Create / Edit form ── */}
      {showForm && (
        <Card>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4">
            {editingId ? 'Edit Corridor' : 'Register New Corridor'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Left: metadata */}
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corridor Name *</label>
                <input className={CLS} required placeholder="e.g. Escravos–Warri Trunk Line"
                  value={form.name} onChange={setF('name')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pipeline ID *</label>
                <input className={CLS} required placeholder="e.g. PL-EWT-001"
                  value={form.pipeline_id} onChange={setF('pipeline_id')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Product Type</label>
                <select className={CLS} value={form.product_type} onChange={setF('product_type')}>
                  {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Buffer Width (m)</label>
                <input className={CLS} type="number" min="10" max="500"
                  value={form.buffer_width_m} onChange={setF('buffer_width_m')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Operator / Company</label>
                <input className={CLS} placeholder="e.g. NNPC, Shell, TotalEnergies"
                  value={form.operator} onChange={setF('operator')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monitoring Frequency</label>
                <select className={CLS} value={form.monitoring_frequency} onChange={setF('monitoring_frequency')}>
                  {MONITORING_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>

              {/* Geometry hint */}
              {!editingId && !drawnLayer && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
                  👉 Draw the corridor boundary on the map →
                </div>
              )}
              {!editingId && drawnLayer && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5 text-xs text-green-800 dark:text-green-300">
                  <Check className="w-3.5 h-3.5 inline mr-1" />Boundary drawn — fill in metadata and save
                </div>
              )}
              {editingId && !drawnLayer && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2.5 text-xs text-blue-800 dark:text-blue-300">
                  Optionally redraw the boundary on the map to update it, or save to update metadata only.
                </div>
              )}
              {editingId && drawnLayer && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5 text-xs text-green-800 dark:text-green-300">
                  <Check className="w-3.5 h-3.5 inline mr-1" />New boundary drawn — will replace existing geometry on save
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <Button type="submit" loading={saving} disabled={!editingId && !drawnLayer}>
                  {editingId ? 'Update Corridor' : 'Save Corridor'}
                </Button>
                <Button type="button" variant="outline" onClick={closeForm} disabled={saving}>Cancel</Button>
              </div>
            </form>

            {/* Right: Leaflet draw map */}
            <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: 420 }}>
              <MapContainer center={[20, 75]} zoom={4} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
                />
                {/* all existing corridors as grey reference */}
                {corridors.filter(hasRealGeometry).map((c) => (
                  <Polygon
                    key={c.id}
                    positions={c.geometry!.coordinates[0].map((pt) => [pt[1], pt[0]] as [number, number])}
                    pathOptions={{
                      color: c.id === editingId ? '#f97316' : '#9ca3af',
                      fillOpacity: c.id === editingId ? 0.15 : 0.08,
                      weight: c.id === editingId ? 2 : 1.5,
                      dashArray: c.id === editingId ? undefined : '4 4',
                    }}
                  />
                ))}
                <FeatureGroup>
                  <EditControl
                    position="topright"
                    onCreated={(e: any) => setDrawnLayer(e.layer)}
                    draw={{
                      rectangle:    false,
                      circle:       false,
                      circlemarker: false,
                      marker:       false,
                      polyline:     true,
                      polygon:      true,
                    }}
                    edit={{ edit: false, remove: false }}
                  />
                </FeatureGroup>
                {/* fit to editing corridor on open */}
                {editingId && validCorridors.filter((c) => c.id === editingId).length > 0 && (
                  <FitBounds corridors={validCorridors.filter((c) => c.id === editingId)} />
                )}
              </MapContainer>
            </div>
          </div>
        </Card>
      )}

      {/* ── List view ── */}
      {!showForm && viewMode === 'list' && (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {corridors.length} corridor{corridors.length !== 1 ? 's' : ''} registered
              {corridors.some((c) => !hasRealGeometry(c)) && (
                <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">
                  — {corridors.filter((c) => !hasRealGeometry(c)).length} missing boundary geometry (click ✏ to redraw)
                </span>
              )}
            </p>
          </div>
          {loading ? (
            <div className="flex justify-center py-14"><Spinner size="lg" /></div>
          ) : corridors.length === 0 ? (
            <div className="text-center py-14">
              <Flame className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No corridors registered</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">Click <strong>Add Corridor</strong> to draw the first pipeline RoW boundary</p>
              <Button onClick={openCreate}><PlusCircle className="w-4 h-4 mr-1" /> Add Corridor</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {['Corridor', 'Pipeline ID', 'Product', 'Buffer (m)', 'Operator', 'Frequency', 'Boundary', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {corridors.map((c) => {
                    const geo = hasRealGeometry(c);
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{c.name}</td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-600 dark:text-gray-400">{c.metadata?.pipeline_id ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 capitalize">{c.metadata?.product_type?.replace(/_/g, ' ') ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.metadata?.buffer_width_m ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{c.metadata?.operator ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 capitalize">{c.monitoring_frequency}</td>
                        <td className="px-4 py-3">
                          {geo
                            ? <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400"><Check className="w-3 h-3" /> Set</span>
                            : <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">⚠ Missing — click ✏ to draw</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => openEdit(c)}
                              className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-600" title="Edit corridor">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(c.id, c.name)}
                              className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600" title="Delete corridor">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Map view ── */}
      {!showForm && viewMode === 'map' && (
        <Card padding="none">
          <div style={{ height: 560 }} className="rounded-lg overflow-hidden">
            <MapContainer center={[20, 75]} zoom={4} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              {validCorridors.length > 0 && <FitBounds corridors={validCorridors} />}
              {validCorridors.map((c) => (
                <Polygon
                  key={c.id}
                  positions={c.geometry!.coordinates[0].map((pt) => [pt[1], pt[0]] as [number, number])}
                  pathOptions={{ color: '#f97316', weight: 2, fillOpacity: 0.2 }}
                >
                  <Popup>
                    <div className="space-y-1 text-sm min-w-[180px]">
                      <p className="font-semibold">{c.name}</p>
                      {c.metadata?.pipeline_id && <p className="text-xs text-gray-600">🛢 {c.metadata.pipeline_id}</p>}
                      {c.metadata?.product_type && <p className="text-xs text-gray-600 capitalize">⛽ {c.metadata.product_type.replace(/_/g, ' ')}</p>}
                      {c.metadata?.operator && <p className="text-xs text-gray-600">🏢 {c.metadata.operator}</p>}
                      {c.metadata?.buffer_width_m && <p className="text-xs text-gray-600">📐 Buffer: {c.metadata.buffer_width_m} m</p>}
                      {c.area_hectares != null && <p className="text-xs text-gray-600">📏 {Number(c.area_hectares).toFixed(1)} ha</p>}
                      <p className="text-xs text-gray-600">📡 Monitoring: {c.monitoring_frequency}</p>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => openEdit(c)} className="text-xs text-blue-600 underline">Edit</button>
                        <button onClick={() => handleDelete(c.id, c.name)} className="text-xs text-red-600 underline">Delete</button>
                      </div>
                    </div>
                  </Popup>
                </Polygon>
              ))}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-[1000]">
                  <Spinner />
                </div>
              )}
            </MapContainer>
          </div>
          {corridors.some((c) => !hasRealGeometry(c)) && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-amber-600 dark:text-amber-400">
              ⚠ {corridors.filter((c) => !hasRealGeometry(c)).length} corridor(s) not shown — boundary geometry missing.
              Switch to <button className="underline" onClick={() => setViewMode('list')}>List view</button> to fix them.
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default CorridorBoundariesPage;

// Made with Bob
